import {HttpClient, HttpParams} from '@angular/common/http';
import {Inject, Injectable, Optional} from '@angular/core';
import * as _ from 'lodash-es';
import {defer, forkJoin, MonoTypeOperatorFunction, Observable, of, race, Subject, switchMap} from 'rxjs';
import {catchError, filter, finalize, map, share, tap} from 'rxjs/operators';
import {extractListResponse, handleResponseError, ListResponse} from '../../common/api-helpers';
import {AssetClass} from '../../enums/asset-class';
import {Exchange} from '../../enums/exchange';
import {QueryLangFilterOperator} from '../../enums/query-lang-filter-operator';
import {SortDirection} from '../../enums/sort-direction';
import {CommonListFilterQueryParamsFactory} from '../../factories/common-list-query-params.factory';
import {CommonListFilter} from '../../models/filters/common-list-filter';
import {FilterCriterion} from '../../models/filters/filter-criterion';
import {QueryLangFilterCriterion} from '../../models/filters/query-lang-filter-criterion';
import {ResponseErrorMessages} from '../../models/response-error-message.interface';
import {Security} from '../../models/security';
import {formatSecurityKey, isSecurityKeysEqual, parseSecurityKey, SecurityKey} from '../../models/security-key';
import {AppConfigService} from '../../services/config.service';
import {AutocompleteProvider, AutocompleteProviderConfig} from '../autocomplete';
import {CUSTOM_SECURITY_REQUEST_HANDLERS, CustomSecurityRequestHandler} from './custom-security-request';
import {
    SECURITY_CURRENCY_FILTER_FIELD_NAMES,
    SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
    SECURITY_SYMBOL_FILTER_FIELD_NAMES
} from './security-filter-criterion-utils';
import {SecurityStreamingService} from './security-streaming';

interface PendingSecurityRequest {
    request$: Observable<Security>;
    subject$: Subject<Security>;
}

// [TT-875] Don't fetch inactive securities from API.
const SECURITY_FILTER_ACTIVE = new QueryLangFilterCriterion({
    fieldNames: ['active'],
    operator:  QueryLangFilterOperator.EQ,
    query:     true
});

// [HED-930] Use Kraken only securities
const SECURITY_FILTER_KRAKEN_FUT = new QueryLangFilterCriterion<Security>({
    fieldNames: ['securityExchange'],
    operator:  QueryLangFilterOperator.EQ,
    query:     Exchange.KRAKEN_FUT
});
// [HED-930] Use Kraken only securities
const SECURITY_FILTER_PERPETUAL = new QueryLangFilterCriterion<Security>({
    fieldNames: ['assetClass'],
    operator:  QueryLangFilterOperator.EQ,
    query:     AssetClass.PER
});

@Injectable()
export class SecurityService implements AutocompleteProvider<Security> {
    private static responseErrorMessages: ResponseErrorMessages = {};

    protected readonly securitiesUrl: string = this.appConfigService.api['securities'];
    protected readonly securitiesDict: {[securityKeyStr: string]: Security} = {};
    private readonly pendingSecurityRequestByKeyStr: {[securityKeyStr: string]: PendingSecurityRequest} = {};
    private readonly securityInvalidationByKeyStr: {[securityKeyStr: string]: Observable<SecurityKey>} = {};
    private readonly invalidateSecurity$ = new Subject<SecurityKey>();

    constructor(
        protected httpClient: HttpClient,
        protected appConfigService: AppConfigService,

        @Optional()
        protected securityStreamingService?: SecurityStreamingService,

        @Optional()
        @Inject(CUSTOM_SECURITY_REQUEST_HANDLERS)
        protected customSecurityRequestHandlers?: CustomSecurityRequestHandler[]
    ) {}

    public getSecurities(securitiesFilter: CommonListFilter): Observable<ListResponse<Security>> {
        let result$: Observable<ListResponse<Security>>;

        const customSecurityRequestHandler = this.customSecurityRequestHandlers?.find(handler =>
            handler.isRestFilterMatch(securitiesFilter)
        );

        if (customSecurityRequestHandler) {
            result$ = customSecurityRequestHandler.fetch(securitiesFilter);
        } else {
            const canReplaceSecurityRequestWithStreaming = this.securityStreamingService
                && this.securityStreamingService.canReplaceSecurityRequestWithStreaming(securitiesFilter);

            if (canReplaceSecurityRequestWithStreaming) {
                result$ = this.securityStreamingService.emulateSecurityRestRequest(securitiesFilter).pipe(
                    map(response => ({
                        ...response,
                        data: response.data.filter(this.isSecurityMatchesRequiredFilters)
                    }))
                );
            } else {
                let requestParams: HttpParams;

                if (securitiesFilter) {
                    securitiesFilter = this.applySecuritiesRequiredFilters(securitiesFilter);

                    const params = CommonListFilterQueryParamsFactory.createQueryParamsAsString(securitiesFilter);
                    requestParams = new HttpParams({fromString: params});
                }

                result$ = this.httpClient.get<ListResponse<Security>>(this.securitiesUrl, {params: requestParams}).pipe(
                    map(extractListResponse),
                    catchError(handleResponseError)
                );
            }
        }

        return result$.pipe(
            tap(listResponse => this.cacheSecurities(listResponse.data))
        );
    }

    public getSecuritiesBySecurityKeys(securityKeys: SecurityKey[]): Observable<Security[]> {
        const resultSecurities = new Array(securityKeys.length);

        const securityKeysMeta = securityKeys.reduce(
            (acc, securityKey, index) => {
                const knownSecurity = this.getSecurityFromCacheBySecurityKey(securityKey);

                if (knownSecurity) {
                    resultSecurities[index] = knownSecurity;
                } else if (this.securityStreamingService.canGetSecurityBySecurityKeyFromStreaming(securityKey)) {
                    acc.streaming.push(securityKey);
                } else {
                    acc.other.push(securityKey);
                }

                acc.indexes.set(securityKey, index);

                return acc;
            },
            {
                streaming: [] as SecurityKey[],
                other: [] as SecurityKey[],
                indexes: new WeakMap<SecurityKey, number>
            }
        );

        const streams: Array<Observable<any>> = [];

        const processSecuritiesResponse = (reqOrigSecurityKeys: SecurityKey[]): MonoTypeOperatorFunction<Security[]> =>
            tap(securities => {
                securities.forEach((security, index) => {
                    const origSecurityKey = reqOrigSecurityKeys[index];
                    const origIndex = securityKeysMeta.indexes.get(origSecurityKey);

                    resultSecurities[origIndex] = security;
                });
            });

        if (securityKeysMeta.streaming.length) {
            const req$ = this.securityStreamingService.getSecuritiesBySecurityKeys(securityKeysMeta.streaming);

            streams.push(
                req$.pipe(
                    tap(securities => this.cacheSecurities(securities)),
                    processSecuritiesResponse(securityKeysMeta.streaming)
                )
            );
        }

        if (securityKeysMeta.other.length) {
            const requests: Array<Observable<Security>> = securityKeysMeta.other.map(securityKey =>
                this.getSecurityBySecurityKey(securityKey)
            );
            const combinedReq$ = forkJoin(requests);

            streams.push(
                // At the moment securities are already cached by getSecurityBySecurityKey.
                combinedReq$.pipe(processSecuritiesResponse(securityKeysMeta.other))
            );
        }

        return streams.length
            ? forkJoin(streams).pipe(
                map(() => resultSecurities)
            )
            : of(resultSecurities);
    }

    public getSecurityBySecurityKey(securityKey: SecurityKey): Observable<Security> {
        return defer(() => {
            const knownSecurity = this.getSecurityFromCacheBySecurityKey(securityKey);

            return knownSecurity !== undefined
                ? of(knownSecurity)
                : this.fetchSecurityBySecurityKey(securityKey);
        });
    }

    public cacheSecurity(security: Security): void {
        const securityKeyStr: string = formatSecurityKey(security);

        // This field can be not defined in API in old projects.
        security.securityKey = security.securityKey || securityKeyStr;

        this.securitiesDict[securityKeyStr] = security || null;

        const pendingRequest = this.pendingSecurityRequestByKeyStr[securityKeyStr];

        if (pendingRequest) {
            pendingRequest.subject$.next(security);
            pendingRequest.subject$.complete();
        }
    }

    public cacheSecurities(securities: Security[]): void {
        securities.forEach(security => this.cacheSecurity(security));
    }

    public getSecurityFromCacheBySecurityKey(securityKey: SecurityKey): Security {
        return this.getSecurityFromCacheBySecurityKeyStr(formatSecurityKey(securityKey));
    }

    public getSecurityFromCacheBySecurityKeyStr(securityKeyStr: string): Security {
        return this.securitiesDict[securityKeyStr];
    }

    public autocomplete(term: string, config?: AutocompleteProviderConfig<Security>): Observable<Security[]> {
        config = _.assign({
            termFieldNames: ['symbol', 'name', 'shortName'],
            sortFieldName: 'symbol',
            filters: [],
            provider: null
        } as AutocompleteProviderConfig<Security>, config);

        const filterCriterion = new QueryLangFilterCriterion({
            fieldNames: config.termFieldNames,
            operator: QueryLangFilterOperator.LIKE,
            query: term
        });

        const securitiesFilter: CommonListFilter = {
            filters: [filterCriterion, ...config.filters],
            pageIndex: 0,
            pageSize: 10,
            sortColumn: config.sortFieldName,
            sortDirection: SortDirection.ASC
        };

        return this.getSecurities(securitiesFilter).pipe(
            map((response: ListResponse<Security>) => response.data)
        );
    }

    /**
     * Returns the {@link Observable} which emits every time the specified security needs to be updated
     * due to inner {@link invalidateSecurity$} event.
     * @param {SecurityKey} securityKey
     * @returns {Observable<SecurityKey>>}
     */
    public getInvalidationStreamBySecurityKey(securityKey: SecurityKey): Observable<SecurityKey> {
        const securityKeyStr = formatSecurityKey(securityKey);

        const createInvalidationSubject = (): Observable<SecurityKey> => {
            const init = <T>(initialization: (source: Observable<T>) => void) =>
                (source: Observable<T>): Observable<T> => {
                    initialization(source);

                    return source;
                };

            const storeStream = (s$: Observable<SecurityKey>) => this.securityInvalidationByKeyStr[securityKeyStr] = s$;
            const deleteStream = () => delete this.securityInvalidationByKeyStr[securityKeyStr];

            return this.invalidateSecurity$.pipe(
                filter(eventSecurityKey => isSecurityKeysEqual(eventSecurityKey, securityKey)),
                init(storeStream),
                finalize(deleteStream),
                share()
            );
        };

        return defer(() =>
            this.securityInvalidationByKeyStr[securityKeyStr]
            || createInvalidationSubject()
        );
    }

    public getDefaultSecurity(filters?: FilterCriterion[]): Observable<Security> {
        const defaultSecurityKeyFromConfig = parseSecurityKey(this.appConfigService.appConfig.defaultSecurityKey);
        const getDefaultSecurityListFilter = (): CommonListFilter => ({
            filters:       filters || [
                new QueryLangFilterCriterion<Security>({
                    fieldNames: ['active'],
                    operator:   QueryLangFilterOperator.EQ,
                    query:      true
                })
            ],
            pageIndex:     0,
            pageSize:      1,
            sortColumn:    null,
            sortDirection: null
        });

        return this.getSecurityBySecurityKey(defaultSecurityKeyFromConfig).pipe(
            switchMap(security =>
                security
                    ? of(security)
                    : this.getSecurities(getDefaultSecurityListFilter()).pipe(
                        map(response => response.data[0])
                    )
            )
        );
    }

    protected createSecurityRequestBySecurityKey(securityKey: SecurityKey): Observable<Security> {
        const filterCriterions: QueryLangFilterCriterion[] = [
            new QueryLangFilterCriterion({
                fieldNames: SECURITY_SYMBOL_FILTER_FIELD_NAMES,
                operator:   QueryLangFilterOperator.EQ,
                query:      securityKey.symbol
            }),
            new QueryLangFilterCriterion({
                fieldNames: SECURITY_EXCHANGE_FILTER_FIELD_NAMES,
                operator:   QueryLangFilterOperator.EQ,
                query:      securityKey.securityExchange
            }),
            new QueryLangFilterCriterion({
                fieldNames: SECURITY_CURRENCY_FILTER_FIELD_NAMES,
                operator:   QueryLangFilterOperator.EQ,
                query:      securityKey.currency
            })
        ];

        const listFilter: CommonListFilter = {
            filters:       filterCriterions,
            pageIndex:     0,
            pageSize:      1,
            sortColumn:    null,
            sortDirection: null
        };

        return this.getSecurities(listFilter).pipe(
            map((securitiesResponse: ListResponse<Security>) => securitiesResponse.data[0])
        );
    }

    protected applySecuritiesRequiredFilters(securitiesFilter: CommonListFilter): CommonListFilter {
        return {
            ...securitiesFilter,
            filters: [
                ...securitiesFilter.filters || [],
                SECURITY_FILTER_ACTIVE, // [TT-875] Don't fetch inactive securities from API.
                SECURITY_FILTER_KRAKEN_FUT, // [HED-930] Use Kraken only securities
                SECURITY_FILTER_PERPETUAL // [HED-930] Use perpetual only securities
            ]
        };
    }

    protected isSecurityMatchesRequiredFilters(security: Security): boolean {
        return !!security.active; // [TT-875] Don't fetch inactive securities from API.
    }

    protected invalidateSecurityBySecurityKey(securityKey: SecurityKey): void {
        this.invalidateSecurity$.next(securityKey);
    }

    private createSecurityPendingRequestByKey(securityKey: SecurityKey): PendingSecurityRequest {
        const securityKeyStr: string = formatSecurityKey(securityKey);
        const subject$ = new Subject<Security>();
        const request$ = race(
            this.createSecurityRequestBySecurityKey(securityKey),
            subject$
        ).pipe(
            finalize(() => delete this.pendingSecurityRequestByKeyStr[securityKeyStr]),
            share()
        );

        const pendingRequest: PendingSecurityRequest = {
            request$,
            subject$
        };

        this.pendingSecurityRequestByKeyStr[securityKeyStr] = pendingRequest;

        return pendingRequest;
    }

    private fetchSecurityBySecurityKey(securityKey: SecurityKey): Observable<Security> {
        return defer(() => {
            const securityKeyStr: string = formatSecurityKey(securityKey);
            const pendingRequest = this.pendingSecurityRequestByKeyStr[securityKeyStr]
                || this.createSecurityPendingRequestByKey(securityKey);

            return pendingRequest.request$;
        });
    }

}
